import React, { Component } from 'react';
import { bool, shape, string } from 'prop-types';
import { withRouter } from 'next/router';
import { connect } from 'react-redux';
import Heart from '/components/Icons/Heart';
import SaveStoryMutation from '/components/SaveStoryMutation';
import DeleteSaveStoryMutation from '/components/DeleteSaveStoryMutation';
import GetSavedStoryQuery from '/components/GetSavedStoryQuery';
import { SITE_KEY } from 'constants/site';
import { GET_SAVED_STORY } from 'graphql/getSavedStory';

const SAVE_MESSAGE_DELAY = 2500; // milliseconds

export class SaveStoryAction extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOptimisticallySaved: false,
      buttonText: ''
    };
    this.handleClick = this.handleClick.bind(this);
    this.timeout = undefined;
  }

  componentWillUnmount() {
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
  }

  handleClick(e, storyAction, isOptimisticSave) {
    e.preventDefault();
    const { resourceId, user } = this.props;
    const variables = {
      auth0User: user.auth0User,
      resourceId,
      site: SITE_KEY
    };

    this.setState(
      {
        isOptimisticallySaved: isOptimisticSave,
        buttonText: isOptimisticSave ? 'Saving' : 'Unsaved'
      },
      () => {
        storyAction({
          variables,
          refetchQueries: [
            {
              query: GET_SAVED_STORY,
              variables: {
                auth0User: user.auth0User,
                resourceId,
                site: SITE_KEY
              }
            }
          ]
        }).then(() => {
          if (this.timeout) {
            window.clearTimeout(this.timeout);
          }
          this.timeout = window.setTimeout(() => {
            this.setState({
              buttonText: ''
            });
          }, SAVE_MESSAGE_DELAY);
        });
      }
    );
  }

  render() {
    const { resourceId, user, router } = this.props;
    const { isOptimisticallySaved, buttonText } = this.state;
    const RedirectLink = (
      <a
        className="SaveStoryAction"
        href="#save-story"
        onClick={e => {
          e.preventDefault();
          router.push(
            `/sign-in?redirect_uri=${encodeURIComponent(window.location.href)}`
          );
        }}
        data-gtm-placement="save story button"
        data-gtm-story-action="save story (unauthenticated)"
        rel="nofollow"
      >
        <Heart isOutlined display="inline-block" />
        Save
      </a>
    );

    if (!user) {
      // If the user is not signed render a button that redirects to auth
      return RedirectLink;
    }

    return (
      <GetSavedStoryQuery
        auth0User={!user ? null : user.auth0User}
        resourceId={resourceId}
        site={SITE_KEY}
      >
        {({ data, error, loading }) => {
          // There was an error don't render the save story button
          if (!data || loading || error) {
            return RedirectLink;
          }

          const [entry] = data.getSaveStory || [];
          // Story is saved if query returns a resourceID or we have isOptimisticallySaved set in the state
          const isStorySaved =
            (entry && entry.resourceId === resourceId) || isOptimisticallySaved;

          // If we want the text version of the button, we change the initial
          // based on if the story is already saved.
          const initialButtonText = isStorySaved ? 'Saved' : 'Save';

          return (
            <DeleteSaveStoryMutation>
              {deleteStoryMutation => (
                <SaveStoryMutation>
                  {saveStoryMutation => (
                    <a
                      className="SaveStoryAction"
                      href="#save-story"
                      onClick={e => {
                        e.preventDefault();
                        const isSaveAction = !isStorySaved;
                        this.handleClick(
                          e,
                          isSaveAction
                            ? saveStoryMutation
                            : deleteStoryMutation,
                          isSaveAction
                        );
                      }}
                      data-gtm-story-action={
                        isStorySaved ? 'unsave story' : 'save story'
                      }
                    >
                      <Heart
                        isOutlined={!isStorySaved}
                        display="inline-block"
                      />
                      {buttonText || initialButtonText}
                    </a>
                  )}
                </SaveStoryMutation>
              )}
            </DeleteSaveStoryMutation>
          );
        }}
      </GetSavedStoryQuery>
    );
  }
}

SaveStoryAction.propTypes = {
  resourceId: string.isRequired,
  router: shape({
    asPath: string
  }).isRequired,
  user: shape({
    auth0User: string,
    email: string,
    emailVerified: bool,
    name: string,
    nickname: string,
    picture: string
  })
};

SaveStoryAction.defaultProps = {
  user: undefined
};

const mapStateToProps = state => {
  const { user } = state;
  return {
    user
  };
};

export default connect(mapStateToProps)(withRouter(SaveStoryAction));
